import { Action, createActionGroup, createFeature, createFeatureSelector, createReducer, on } from '@ngrx/store';
import { Dictionary, EntityState, createEntityAdapter } from '@ngrx/entity';
import { jsonCopyObject } from 'globals/helpers/helpers';
import { eventsActions } from './events.era';
import { AppNotification, JoiApp, JoiAppInfo, SurveySubmission } from 'globals/store/app.data';

const LOAD_APP_INFOS = '[apps] LOAD_APP_INFOS'
const REMOVE_APP_INFO = '[apps] REMOVE_APP_INFO'
const LOAD_APP = '[apps] LOAD_APP'
const LOAD_SURVEY_RESPONSES = '[apps] LOAD_SURVEY_RESPONSES'
export const LOAD_APP_NOTIFICATIONS = '[apps] LOAD APP NOTIFICATIONS'
export const REMOVE_APP_NOTIFICATIONS = '[apps] REMOVE APP NOTIFICATIONS'

export interface AppsState extends EntityState<JoiAppInfo> {
  apps: Dictionary<JoiApp>
  responses: Dictionary<SurveySubmission[]>,
  notificationsByAppId: Dictionary<Dictionary<AppNotification>>
}

const getDefaultAppsState = (): AppsState => ({
  ids: [],
  entities: {},
  apps: {},
  responses: {},
  notificationsByAppId: {} 
})

export const adapter = createEntityAdapter<JoiAppInfo>()
export const makeInitialState = (): AppsState => adapter.getInitialState(getDefaultAppsState())

export const appsActions = createActionGroup({
  source: 'Apps',
  events: {
    'Load App Infos': (appInfos: JoiAppInfo[]) => ({ appInfos }),
    'Remove App Info': (id: string) => ({ id }),
    'Load App': (app: JoiApp) => ({ app }),
    'Load Survey Responses': (surveyId: string, responses: SurveySubmission[]) => ({ surveyId, responses }),
    'Load App Notifications': (notifications: AppNotification[]) => ({ notifications }),
    'Remove App Notifications': (notifications: AppNotification[]) => ({ notifications }),
  }
})

export const appsReducer = createReducer(
  makeInitialState(),
  on(appsActions.loadAppInfos, (state, { appInfos }) => adapter.upsertMany(appInfos, state)),
  on(appsActions.removeAppInfo, (state, { id }) => adapter.removeOne(id, state)),
  on(appsActions.loadApp, (state, { app }) => {
    const apps = { ...state.apps }
    apps[app.id] = app
    return { ...state, apps }
  }),
  on(appsActions.loadSurveyResponses, (state, { surveyId, responses }) => {
    const newResponses = { ...state.responses }
    newResponses[surveyId] = responses
    return { ...state, responses: newResponses }
  }),
  on(appsActions.loadAppNotifications, (state, { notifications }) => {
    const notificationsByAppId = jsonCopyObject(state.notificationsByAppId) // easiest way to deep copy this
    notifications.forEach(n => {
      if (!notificationsByAppId[n.appId]) notificationsByAppId[n.appId] = {}
      notificationsByAppId[n.appId][n.id] = n
    })
    return { ...state, notificationsByAppId }
  }),
  on(appsActions.removeAppNotifications, (state, { notifications }) => {
    const notificationsByAppId = jsonCopyObject(state.notificationsByAppId) // easiest way to deep copy this
    notifications.forEach(n => {
      if (notificationsByAppId[n.appId]) delete notificationsByAppId[n.appId][n.id]
    })
    return { ...state, notificationsByAppId }
  }),
  on(eventsActions.unloadEvent, (state) => makeInitialState()),
)

export const appsFeature = createFeature({
  name: 'apps',
  reducer: appsReducer
})

export const appsStateSelector = createFeatureSelector<AppsState>(appsFeature.name)
